import React, {
    createContext, useEffect, useReducer, useMemo
} from 'react';

const initialState = {
    instances: {},
    latestItemId: null,
    itemToCloseId: null,
    parentBounding: null,
}

const actions = {
    SET_IS_VISIBLE: 'SET_IS_VISIBLE',
    CLOSE_SELECT_POPUP_INSTANCE: 'CLOSE_SELECT_POPUP_INSTANCE',
    REMOVE_INSTANCE: 'REMOVE_INSTANCE',
    CREATE_SELECT_POPUP_INSTANCE_BY_PARENT_POSITION: 'CREATE_SELECT_POPUP_INSTANCE_BY_PARENT_POSITION',
    SET_SELECT_POPUP_POSITION_AND_CREATE_NEW_SELECT_POPUP_INSTANCE:
        'SET_SELECT_POPUP_POSITION_AND_CREATE_NEW_SELECT_POPUP_INSTANCE',
    SET_SELECTED_ITEM: 'SET_SELECTED_ITEM',
}

const reducer = (state, action) => {
    switch (action.type) {
        case actions.SET_IS_VISIBLE:
            return state.latestItemId ? {
                ...state,
                instances: {
                    ...state.instances,
                    [state.latestItemId]: {
                        ...state.instances[state.latestItemId],
                        isVisible: action.payload
                    }
                }
            } : state;
        case actions.CLOSE_SELECT_POPUP_INSTANCE:
            return {
                ...state,
                latestItemId: state.latestItemId === action.id ? null : state.latestItemId,
                itemToCloseId: action.id,
                instances: { ...state.instances, [action.id]: { ...state.instances[action.id], isVisible: false } }
            };
        case actions.REMOVE_INSTANCE:
            return {
                ...state,
                itemToCloseId: null,
                instances: Object.fromEntries(Object.entries(state.instances).filter(([key]) => key !== action.payload))
            }
        case actions.CREATE_SELECT_POPUP_INSTANCE_BY_PARENT_POSITION:
            return {
                ...state,
                parentBounding: action.parentBounding,
                latestItemId: action.id
            };
        case actions.SET_SELECT_POPUP_POSITION_AND_CREATE_NEW_SELECT_POPUP_INSTANCE:
            return state.latestItemId ? {
                ...state,
                instances: {
                    ...state.instances,
                    [state.latestItemId]: {
                        selectedItem: null,
                        selectPopupTop: action.selectPopupTop,
                        selectPopupLeft: action.selectPopupLeft,
                        selectPopupBottom: action.selectPopupBottom,
                        selectPopupRight: action.selectPopupRight,
                        isVisible: false
                    }
                }
            } : state;
        case actions.SET_SELECTED_ITEM:
            return state.latestItemId ? {
                ...state,
                instances: {
                    ...state.instances,
                    [state.latestItemId]: {
                        ...state.instances[state.latestItemId],
                        selectedItem: action.payload
                    }
                }
            } : state;
        default:
            return state;
    }
}

export const SelectPopupContext = createContext();

const SelectPopupContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);


    useEffect(() => {
        state.parentBounding?.top && calculateAndSetSelectPopupPosition(state?.parentBounding);

        const repositionSelectPopupOnResize = () => {
            calculateAndSetSelectPopupPosition(state.parentBounding);
        }

        window.addEventListener('resize', repositionSelectPopupOnResize);
        return () => { window.removeEventListener('resize', repositionSelectPopupOnResize); }
    }, [state.parentBounding]);

    const calculateAndSetSelectPopupPosition = (parentBounding) => {
        // const pageWidth = window.innerWidth;
        const pageHeight = window.innerHeight;

        // calcuate vertical position
        let top;
        let bottom;
        let left;
        let right;

        const triggerElementHeight = (parentBounding?.bottom - parentBounding?.top);
        const verticalGap = 15;
        if (window.innerWidth <= 768) {
            bottom = 0;
        }
        else {
            // eslint-disable-next-line no-lonely-if
            if (parentBounding?.top <= pageHeight / 2) {
                // if trigger element is in top half of the page
                parentBounding?.top && (top = parentBounding.top + triggerElementHeight + verticalGap);
            }
            else {
                // if trigger element is in bottom half of the page
                parentBounding?.bottom &&
                    (bottom = pageHeight - parentBounding.bottom + triggerElementHeight + verticalGap);
            }
        }

        // calculate horizontal position
        if (parentBounding?.right <= 256 + 4 + 20) {
            // if it's left side of the page, render toward right
            left = parentBounding.left;
        }
        else {
            parentBounding?.right &&
                (left = parentBounding.right - (256 + 4));
            // 256px = 16rem => width of the popup, 4px is an additional margin
        }

        dispatch({
            type: actions.SET_SELECT_POPUP_POSITION_AND_CREATE_NEW_SELECT_POPUP_INSTANCE,
            selectPopupTop: top,
            selectPopupBottom: bottom,
            selectPopupRight: right,
            selectPopupLeft: left
        });
    }


    // (transition related)
    useEffect(() => {
        const makeVisibleTimeout = setTimeout(() => {
            if (state?.instances?.[state?.latestItemId] &&
                (state?.instances?.[state?.latestItemId]?.isVisible === false)) {
                dispatch({ type: actions.SET_IS_VISIBLE, payload: true });
            }
        }, 100);
        return () => { clearTimeout(makeVisibleTimeout) }
    }, [state.instances, state.latestItemId]);


    // (transition related) to close with transition
    useEffect(() => {
        const makeVisibleTimeout = setTimeout(() => {
            if (state.itemToCloseId) {
                dispatch({ type: actions.REMOVE_INSTANCE, payload: state.itemToCloseId });
            }
        }, 100);
        return () => { clearTimeout(makeVisibleTimeout); }
    }, [state.itemToCloseId]);


    const value = useMemo(() => ({
        closeSelectPopupInstance: (id) => { dispatch({ type: actions.CLOSE_SELECT_POPUP_INSTANCE, id }) },
        parentBounding: state.parentBounding,
        openSelectPopupByParentPosition: (parentBounding, id) => {
            dispatch({ type: actions.CREATE_SELECT_POPUP_INSTANCE_BY_PARENT_POSITION, parentBounding, id })
        },
        instances: state.instances,
        latestItemId: state.latestItemId,
        // ⚠️ note: wait 10 ms to prevent from conflict on selectPopup closeclick event listener,
        // (if we use it without setTimeout, then mean while it's removing the content to set
        // second stage's content, it checks existence of the clicked item in the container and
        //  as it's not there, it will closes popup unexpectedly.)
        setSelectedItem: payload => {
            setTimeout(() => {
                dispatch({ type: actions.SET_SELECTED_ITEM, payload })
            }, 10)
        },
    }), [state, dispatch]);

    return (
        <SelectPopupContext.Provider value={value}>
            {children}
        </SelectPopupContext.Provider>
    )
}

export default SelectPopupContextProvider;
